import React, { useState, useEffect } from 'react';
import { HiPlus, HiX, HiArrowRight, HiCash, HiOfficeBuilding, HiDocumentText, HiSwitchVertical, HiPencil, HiCheck } from 'react-icons/hi';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { useNotification } from '../context/NotificationContext';
import { getErrorMessage } from '../utils/errorHandler';
import { formatPKR } from '../utils/currencyFormatter';
import { downloadAccountStatementPdf } from '../utils/accountStatementPdf';
import { API } from '../config';
import axios from 'axios';
import './Accounts.css';

const Accounts = () => {
  const { user } = useAuth();
  const { hasBranches, effectiveBranchId, isAdmin, currentBranch } = useBranch();
  const { success, error } = useNotification();
  const [summary, setSummary] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(user?.business_id || user?.businessId || '');
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showTransactions, setShowTransactions] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsPdfLoading, setTransactionsPdfLoading] = useState(false);
  const [txPage, setTxPage] = useState(1);
  const [txPagination, setTxPagination] = useState({ total: 0, totalPages: 1 });
  const [txFromDate, setTxFromDate] = useState('');
  const [txToDate, setTxToDate] = useState('');
  const [upcomingCheques, setUpcomingCheques] = useState([]);
  const [pendingCheques, setPendingCheques] = useState([]);
  const [showChequesModal, setShowChequesModal] = useState(false);
  const [chequesTab, setChequesTab] = useState('active');
  const [partySearchCheques, setPartySearchCheques] = useState('');
  const [activeCheques, setActiveCheques] = useState([]);
  const [activePagination, setActivePagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [activePage, setActivePage] = useState(1);
  const [activeLoading, setActiveLoading] = useState(false);
  const [clearedCheques, setClearedCheques] = useState([]);
  const [clearedPagination, setClearedPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [clearedPage, setClearedPage] = useState(1);
  const [partySearchCleared, setPartySearchCleared] = useState('');
  const [clearedLoading, setClearedLoading] = useState(false);
  const [approveChequeId, setApproveChequeId] = useState(null);
  const [deletingChequeId, setDeletingChequeId] = useState(null);
  const [confirmDeleteChequeId, setConfirmDeleteChequeId] = useState(null);
  const [approveSplits, setApproveSplits] = useState([{ accountId: '', amount: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [statement, setStatement] = useState([]);
  const [statementLoading, setStatementLoading] = useState(false);
  const [showStatement, setShowStatement] = useState(false);
  const [statementFilters, setStatementFilters] = useState({
    fromDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0],
    accountId: ''
  });

  const [addAccountForm, setAddAccountForm] = useState({
    type: 'bank',
    name: '',
    bankName: '',
    accountNumber: '',
    openingBalance: '0',
    notes: ''
  });
  const [manualEntryForm, setManualEntryForm] = useState({
    accountId: '',
    amount: '',
    direction: 'debit',
    entryType: 'expense',
    description: '',
    transactionDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'cash'
  });
  const [transferForm, setTransferForm] = useState({
    fromAccountId: '',
    toAccountId: '',
    amount: '',
    description: '',
    transactionDate: new Date().toISOString().split('T')[0]
  });

  const businessId = user?.role === 'super_admin' ? selectedBusinessId : (user?.business_id || user?.businessId);
  const businessLabel = user?.role === 'super_admin'
    ? (businesses.find((b) => String(b.id) === String(selectedBusinessId))?.name || 'Business')
    : (user?.business_name || user?.businessName || 'Business');

  const transferAccountList = effectiveBranchId ? accounts : (summary?.accounts || accounts);
  const getAccountTypeLabel = (a) => {
    const type = (a.type || '').toLowerCase();
    if (type === 'bank') {
      const bankName = a.bankName || a.bank_name;
      return bankName ? `Bank – ${bankName}` : 'Bank';
    }
    return type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Account';
  };
  const getTransferAccountOptionLabel = (a, withBalance = false) => {
    const typeLabel = getAccountTypeLabel(a);
    const base = `${a.name} (${typeLabel})`;
    return withBalance ? `${base} – ${formatPKR(a.current_balance ?? a.currentBalance ?? 0)}` : base;
  };
  const transferFromAccount = transferForm.fromAccountId
    ? transferAccountList.find(a => String(a.id) === String(transferForm.fromAccountId))
    : null;
  const transferFromBalance = transferFromAccount != null
    ? parseFloat(transferFromAccount.current_balance ?? transferFromAccount.currentBalance ?? 0)
    : null;
  const transferAmountNum = parseFloat(transferForm.amount);
  const transferAmountExceedsBalance = transferFromBalance != null && !Number.isNaN(transferAmountNum) && transferAmountNum > transferFromBalance;

  useEffect(() => {
    if (user?.role === 'super_admin') {
      axios.get(`${API}/businesses/all`).then(res => {
        const list = res.data.businesses || [];
        setBusinesses(list);
        if (!selectedBusinessId && list.length > 0) setSelectedBusinessId(list[0].id);
      }).catch(() => setBusinesses([]));
    }
  }, [user?.role]);

  useEffect(() => {
    if (businessId) {
      fetchSummary();
      fetchAccounts();
      fetchUpcomingCheques();
      fetchPendingCheques();
    } else {
      setLoading(false);
    }
  }, [businessId, effectiveBranchId]);

  useEffect(() => {
    if (!approveChequeId) return;
    const c = activeCheques.find(x => x.id === approveChequeId);
    if (c) {
      const amt = c?.amount != null ? String(c.amount) : '';
      setApproveSplits([{ accountId: c?.account_id ? String(c.account_id) : '', amount: amt }]);
    }
  }, [approveChequeId, activeCheques]);

  const fetchUpcomingCheques = async (partySearch = '') => {
    if (!businessId) return;
    try {
      const params = new URLSearchParams();
      if (effectiveBranchId) params.set('branchId', effectiveBranchId);
      if (partySearch.trim()) params.set('partySearch', partySearch.trim());
      const url = `${API}/funds/business/${businessId}/upcoming-cheques${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await axios.get(url);
      setUpcomingCheques(res.data.cheques || []);
    } catch (err) {
      setUpcomingCheques([]);
    }
  };

  const fetchPendingCheques = async (partySearch = '') => {
    if (!businessId) return;
    try {
      const params = new URLSearchParams();
      if (effectiveBranchId) params.set('branchId', effectiveBranchId);
      if (partySearch.trim()) params.set('partySearch', partySearch.trim());
      const url = `${API}/funds/business/${businessId}/pending-cheques${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await axios.get(url);
      setPendingCheques(res.data.cheques || []);
    } catch (err) {
      setPendingCheques([]);
    }
  };

  const fetchActiveCheques = async (page = 1, partySearch = '') => {
    if (!businessId) return;
    setActiveLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (effectiveBranchId) params.set('branchId', effectiveBranchId);
      if (partySearch.trim()) params.set('partySearch', partySearch.trim());
      const res = await axios.get(`${API}/funds/business/${businessId}/cheques/active?${params.toString()}`);
      setActiveCheques(res.data.cheques || []);
      setActivePagination(res.data.pagination || { page: 1, limit: 20, total: 0, totalPages: 1 });
      setActivePage(res.data.pagination?.page ?? page);
    } catch (err) {
      setActiveCheques([]);
      setActivePagination({ page: 1, limit: 20, total: 0, totalPages: 1 });
    } finally {
      setActiveLoading(false);
    }
  };

  const fetchClearedCheques = async (page = 1, partySearch = '') => {
    if (!businessId) return;
    setClearedLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (effectiveBranchId) params.set('branchId', effectiveBranchId);
      if (partySearch.trim()) params.set('partySearch', partySearch.trim());
      const res = await axios.get(`${API}/funds/business/${businessId}/cheques/cleared-deleted?${params.toString()}`);
      setClearedCheques(res.data.cheques || []);
      setClearedPagination(res.data.pagination || { page: 1, limit: 20, total: 0, totalPages: 1 });
      setClearedPage(res.data.pagination?.page ?? page);
    } catch (err) {
      setClearedCheques([]);
      setClearedPagination({ page: 1, limit: 20, total: 0, totalPages: 1 });
    } finally {
      setClearedLoading(false);
    }
  };

  const openChequesModal = () => {
    setShowChequesModal(true);
    setChequesTab('active');
    setPartySearchCheques('');
    setPartySearchCleared('');
    setActivePage(1);
    setClearedPage(1);
    fetchActiveCheques(1, '');
  };

  const handleSearchActiveCheques = () => {
    setActivePage(1);
    fetchActiveCheques(1, partySearchCheques);
  };

  const handleSearchClearedCheques = () => {
    setClearedPage(1);
    fetchClearedCheques(1, partySearchCleared);
  };

  const goToActivePage = (page) => {
    if (page < 1 || page > (activePagination.totalPages || 1)) return;
    setActivePage(page);
    fetchActiveCheques(page, partySearchCheques);
  };

  const goToClearedPage = (page) => {
    if (page < 1 || page > (clearedPagination.totalPages || 1)) return;
    setClearedPage(page);
    fetchClearedCheques(page, partySearchCleared);
  };

  const handleDeleteCheque = async (id) => {
    if (!id) return;
    setDeletingChequeId(id);
    try {
      await axios.delete(`${API}/funds/pending-cheques/${id}`);
      success('Cheque deleted');
      setConfirmDeleteChequeId(null);
      fetchActiveCheques(activePage, partySearchCheques);
      fetchUpcomingCheques();
      fetchPendingCheques();
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setDeletingChequeId(null);
    }
  };

  const formatChequeDate = (d) => {
    if (!d) return '—';
    if (typeof d === 'string') return d;
    try { return new Date(d).toISOString().split('T')[0]; } catch { return '—'; }
  };

  const fetchSummary = async () => {
    try {
      let url = `${API}/funds/business/${businessId}/summary`;
      if (effectiveBranchId) url += `?branchId=${effectiveBranchId}`;
      const res = await axios.get(url);
      setSummary(res.data);
    } catch (err) {
      console.error(err);
      setSummary({ totalCash: 0, totalBank: 0, total: 0, accounts: [] });
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    if (!businessId) return;
    try {
      const url = effectiveBranchId
        ? `${API}/funds/business/${businessId}/accounts?branchId=${effectiveBranchId}`
        : `${API}/funds/business/${businessId}/accounts`;
      const res = await axios.get(url);
      setAccounts(res.data.accounts || []);
    } catch (err) {
      console.error(err);
      setAccounts([]);
    }
  };

  const getDefaultTransactionDateRange = () => {
    const to = new Date();
    const from = new Date(to);
    from.setDate(from.getDate() - 6);
    return {
      fromDate: from.toISOString().split('T')[0],
      toDate: to.toISOString().split('T')[0]
    };
  };

  const fetchTransactions = async (accountId, page = 1, fromDate, toDate) => {
    if (!accountId) return;
    setTransactionsLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (fromDate) params.set('fromDate', fromDate);
      if (toDate) params.set('toDate', toDate);
      const res = await axios.get(`${API}/funds/accounts/${accountId}/transactions?${params.toString()}`);
      setTransactions(res.data.transactions || []);
      setTxPagination(res.data.pagination || { total: 0, total_pages: 1 });
    } catch (err) {
      error(getErrorMessage(err));
      setTransactions([]);
      setTxPagination({ total: 0, total_pages: 1 });
    } finally {
      setTransactionsLoading(false);
    }
  };

  const handleAddAccount = async (e) => {
    e.preventDefault();
    if (!hasBranches) {
      error('Create at least one branch before adding accounts. Go to Branches and add a branch.');
      return;
    }
    if (isAdmin && !effectiveBranchId) {
      error('Select a branch from the header dropdown to add an account.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        type: addAccountForm.type,
        name: addAccountForm.name || (addAccountForm.type === 'cash' ? 'Cash' : 'Bank Account'),
        bankName: addAccountForm.type === 'bank' ? addAccountForm.bankName : null,
        accountNumber: addAccountForm.type === 'bank' ? addAccountForm.accountNumber : null,
        openingBalance: parseFloat(addAccountForm.openingBalance) || 0,
        notes: addAccountForm.notes || null
      };
      if (effectiveBranchId) payload.branchId = effectiveBranchId;
      await axios.post(`${API}/funds/business/${businessId}/accounts`, payload);
      success('Account created');
      setShowAddAccount(false);
      setAddAccountForm({ type: 'bank', name: '', bankName: '', accountNumber: '', openingBalance: '0', notes: '' });
      fetchSummary();
      fetchAccounts();
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualEntry = async (e) => {
    e.preventDefault();
    if (!manualEntryForm.accountId) {
      error('Select an account');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/funds/accounts/${manualEntryForm.accountId}/manual-entry`, {
        amount: parseFloat(manualEntryForm.amount),
        direction: manualEntryForm.direction,
        entryType: manualEntryForm.entryType,
        description: manualEntryForm.description || undefined,
        transactionDate: manualEntryForm.transactionDate,
        paymentMethod: manualEntryForm.paymentMethod
      });
      success('Entry recorded');
      setShowManualEntry(false);
      setManualEntryForm({ accountId: '', amount: '', direction: 'debit', entryType: 'expense', description: '', transactionDate: new Date().toISOString().split('T')[0], paymentMethod: 'cash' });
      fetchSummary();
      fetchAccounts();
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    if (!transferForm.fromAccountId || !transferForm.toAccountId || transferForm.fromAccountId === transferForm.toAccountId) {
      error('Select different from and to accounts');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/funds/transfer`, {
        fromAccountId: parseInt(transferForm.fromAccountId),
        toAccountId: parseInt(transferForm.toAccountId),
        amount: parseFloat(transferForm.amount),
        description: transferForm.description || undefined,
        transactionDate: transferForm.transactionDate
      });
      success('Transfer completed');
      setShowTransfer(false);
      setTransferForm({ fromAccountId: '', toAccountId: '', amount: '', description: '', transactionDate: new Date().toISOString().split('T')[0] });
      fetchSummary();
      fetchAccounts();
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const openTransactions = (account) => {
    setSelectedAccount(account);
    setShowTransactions(true);
    const { fromDate, toDate } = getDefaultTransactionDateRange();
    setTxFromDate(fromDate);
    setTxToDate(toDate);
    setTxPage(1);
    fetchTransactions(account.id, 1, fromDate, toDate);
  };

  const applyTransactionFilters = () => {
    if (!selectedAccount) return;
    setTxPage(1);
    fetchTransactions(selectedAccount.id, 1, txFromDate || undefined, txToDate || undefined);
  };

  const goToTransactionsPage = (page) => {
    if (!selectedAccount || page < 1 || page > (txPagination.total_pages ?? txPagination.totalPages ?? 1)) return;
    setTxPage(page);
    fetchTransactions(selectedAccount.id, page, txFromDate || undefined, txToDate || undefined);
  };

  const handleApproveCheque = async (e) => {
    e && e.preventDefault();
    if (!approveChequeId) return;
    const cheque = activeCheques.find(x => x.id === approveChequeId);
    const chequeAmount = cheque ? parseFloat(cheque.amount) || 0 : 0;
    const splits = approveSplits.filter(s => s.accountId && (parseFloat(s.amount) || 0) > 0);
    const sum = splits.reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
    if (Math.abs(sum - chequeAmount) > 0.01) {
      error(`Total (${sum.toFixed(2)}) must equal cheque amount (${chequeAmount.toFixed(2)})`);
      return;
    }
    if (splits.length === 0) {
      error('Add at least one account and amount.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = splits.length === 1 && Math.abs((parseFloat(splits[0].amount) || 0) - chequeAmount) < 0.01
        ? { accountId: parseInt(splits[0].accountId, 10) }
        : { splits: splits.map(s => ({ accountId: parseInt(s.accountId, 10), amount: parseFloat(s.amount) })) };
      await axios.post(`${API}/funds/pending-cheques/${approveChequeId}/approve`, payload);
      success('Cheque approved. Party ledger and account(s) updated.');
      setApproveChequeId(null);
      setApproveSplits([{ accountId: '', amount: '' }]);
      fetchActiveCheques(activePage, partySearchCheques);
      fetchPendingCheques();
      fetchUpcomingCheques();
      fetchSummary();
      fetchAccounts();
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const fetchStatement = async () => {
    if (!businessId) return;
    setStatementLoading(true);
    try {
      let url = `${API}/funds/business/${businessId}/statement?fromDate=${statementFilters.fromDate || ''}&toDate=${statementFilters.toDate || ''}`;
      if (effectiveBranchId) url += `&branchId=${effectiveBranchId}`;
      if (statementFilters.accountId) url += `&accountId=${statementFilters.accountId}`;
      const res = await axios.get(url);
      setStatement(res.data.statement || []);
    } catch (err) {
      console.error(err);
      setStatement([]);
    } finally {
      setStatementLoading(false);
    }
  };

  useEffect(() => {
    if (showStatement && businessId) fetchStatement();
  }, [showStatement, businessId, statementFilters.fromDate, statementFilters.toDate, statementFilters.accountId, effectiveBranchId]);

  const formatType = (t) => (t === 'credit' || t === 'transfer_in') ? 'In' : 'Out';

  const handleDownloadTransactionsPdf = async () => {
    if (!selectedAccount || transactionsPdfLoading) return;
    setTransactionsPdfLoading(true);
    try {
      const rows = (transactions || []).map((tx) => ({
        ...tx,
        account_name: selectedAccount.name,
        account_type: selectedAccount.type
      }));
      await downloadAccountStatementPdf({
        businessName: businessLabel,
        branchName: currentBranch?.name || '—',
        accountFilterLabel: `${selectedAccount.name} (${selectedAccount.type})`,
        fromDate: txFromDate || '—',
        toDate: txToDate || '—',
        rows,
        generatedAt: new Date()
      });
      success('Account statement PDF downloaded.');
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setTransactionsPdfLoading(false);
    }
  };

  if (loading && !summary) {
    return (
      <Layout>
        <div className="accounts-page">
          <h1>Cash & Bank</h1>
          <Loader message="Loading accounts..." variant="page" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="accounts-page">
        <header className="accounts-page-header">
          <div className="accounts-header-top">
            <h1 className="accounts-title">Cash & Bank</h1>
            {user?.role === 'super_admin' && businesses.length > 0 && (
              <select
                value={selectedBusinessId}
                onChange={(e) => setSelectedBusinessId(e.target.value)}
                className="accounts-business-select"
                aria-label="Select business"
              >
                {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            )}
          </div>
          <p className="accounts-subtitle">
            Track cash and bank balances. Record payments, transfers, and manual entries.
          </p>
        </header>

        {/* Balance overview */}
        {summary && (
          <section className="accounts-balance-section" aria-label="Balance overview">
            <div className="accounts-balance-cards">
              <div className="balance-card balance-card--cash">
                <div className="balance-card__icon">
                  <HiCash aria-hidden />
                </div>
                <div className="balance-card__content">
                  <span className="balance-card__label">Total Cash</span>
                  <span className="balance-card__value">{formatPKR(summary.total_cash ?? summary.totalCash ?? 0)}</span>
                </div>
              </div>
              <div className="balance-card balance-card--bank">
                <div className="balance-card__icon">
                  <HiOfficeBuilding aria-hidden />
                </div>
                <div className="balance-card__content">
                  <span className="balance-card__label">Total Bank</span>
                  <span className="balance-card__value">{formatPKR(summary.total_bank ?? summary.totalBank ?? 0)}</span>
                </div>
              </div>
              <div className="balance-card balance-card--total">
                <div className="balance-card__content">
                  <span className="balance-card__label">Total</span>
                  <span className="balance-card__value">{formatPKR(summary.total ?? 0)}</span>
                </div>
              </div>
            </div>
          </section>
        )}

        {!hasBranches && (
          <div className="accounts-banner accounts-banner--warning">
            Create at least one branch before adding accounts. Go to <a href="/branches">Branches</a> to add a branch.
          </div>
        )}

        {/* Primary actions */}
        <section className="accounts-actions-section">
          <div className="accounts-actions">
            <button
              type="button"
              className="accounts-btn accounts-btn--primary"
              onClick={() => setShowAddAccount(true)}
              disabled={!hasBranches}
              title={!hasBranches ? 'Create a branch first' : 'Add a new bank or cash account'}
            >
              <HiPlus className="accounts-btn__icon" /> Add Account
            </button>
            <button type="button" className="accounts-btn accounts-btn--secondary" onClick={() => setShowManualEntry(true)} title="Record expense, deposit, or ATM">
              <HiPencil className="accounts-btn__icon" /> Manual Entry
            </button>
            <button type="button" className="accounts-btn accounts-btn--secondary" onClick={() => setShowTransfer(true)} title="Transfer between accounts">
              <HiSwitchVertical className="accounts-btn__icon" /> Transfer
            </button>
            <button
              type="button"
              className="accounts-btn accounts-btn--secondary"
              onClick={() => setShowStatement(!showStatement)}
              title="View full transaction history"
            >
              <HiDocumentText className="accounts-btn__icon" /> {showStatement ? 'Hide statement' : 'View statement'}
            </button>
          </div>
        </section>

        {/* Cash & Bank statement – all in/out history */}
        {showStatement && (
          <section className="accounts-section accounts-statement-section">
            <div className="accounts-section-header">
              <h2 className="accounts-section-title">Statement</h2>
              <p className="accounts-section-desc">All money in and out: payments, sales, expenses, transfers, and manual entries.</p>
            </div>
            <div className="statement-filters">
              <div className="form-group">
                <label>From date</label>
                <input
                  type="date"
                  value={statementFilters.fromDate}
                  onChange={(e) => setStatementFilters(f => ({ ...f, fromDate: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>To date</label>
                <input
                  type="date"
                  value={statementFilters.toDate}
                  onChange={(e) => setStatementFilters(f => ({ ...f, toDate: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Account</label>
                <select
                  value={statementFilters.accountId}
                  onChange={(e) => setStatementFilters(f => ({ ...f, accountId: e.target.value }))}
                >
                  <option value="">All accounts</option>
                  {(effectiveBranchId ? accounts : (summary?.accounts || accounts)).map(a => (
                    <option key={a.id} value={a.id}>{a.name} ({a.type})</option>
                  ))}
                </select>
              </div>
            </div>
            {statementLoading ? (
              <p className="statement-loading">Loading statement…</p>
            ) : (
              <div className="statement-table-wrap">
                <table className="statement-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Account</th>
                      <th>Reference</th>
                      <th>Description</th>
                      <th className="amount-col">In (PKR)</th>
                      <th className="amount-col">Out (PKR)</th>
                      <th className="amount-col">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statement.length === 0 && (
                      <tr><td colSpan={7} className="empty-row">No transactions in this period.</td></tr>
                    )}
                    {statement.map((row) => (
                      <tr key={row.id}>
                        <td className="date-cell">{row.transaction_date}</td>
                        <td>{row.account_name || '—'} {row.account_type && <span className="account-type-tag">({row.account_type})</span>}</td>
                        <td className="reference-cell">{row.reference_label || '—'}</td>
                        <td className="desc-cell">{row.description || '—'}</td>
                        <td className="amount-col in-col">{(row.type === 'credit' || row.type === 'transfer_in') ? formatPKR(row.amount) : '—'}</td>
                        <td className="amount-col out-col">{(row.type === 'debit' || row.type === 'transfer_out') ? formatPKR(row.amount) : '—'}</td>
                        <td className="amount-col balance-col">{formatPKR(row.balance_after)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* Cheques: single button opens modal (Upcoming by default, tab to Pending) */}
        <section className="accounts-section accounts-section--cheques">
          <div className="accounts-section-header">
            <h2 className="accounts-section-title">Cheques</h2>
            <p className="accounts-section-desc">Pending (date on or before today) and upcoming (no date or post-dated) cheques from party payments.</p>
          </div>
          <div className="accounts-cheques-actions">
            <button
              type="button"
              className="accounts-btn accounts-btn--secondary"
              onClick={openChequesModal}
            >
              <HiDocumentText className="accounts-btn__icon" /> Cheques
              {(pendingCheques.length + upcomingCheques.length) > 0 && (
                <span className="accounts-cheque-badge">{pendingCheques.length + upcomingCheques.length}</span>
              )}
            </button>
          </div>
        </section>

        {/* Single Cheques modal: Pending & Upcoming (paginated) and Cleared & Deleted (paginated) */}
        {showChequesModal && (
          <div className="modal-overlay" onClick={() => !approveChequeId && !confirmDeleteChequeId && setShowChequesModal(false)}>
            <div className="modal-content accounts-modal accounts-cheques-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Cheques</h3>
                <button type="button" className="modal-close" onClick={() => { setShowChequesModal(false); setApproveChequeId(null); setConfirmDeleteChequeId(null); }}><HiX /></button>
              </div>
              <div className="cheques-modal-tabs">
                <button type="button" className={`cheques-tab ${chequesTab === 'active' ? 'active' : ''}`} onClick={() => { setChequesTab('active'); if (activeCheques.length === 0 && !activeLoading) fetchActiveCheques(1, partySearchCheques); }}>
                  Pending & Upcoming
                  {activePagination.total > 0 && <span className="accounts-cheque-badge">{activePagination.total}</span>}
                </button>
                <button type="button" className={`cheques-tab ${chequesTab === 'cleared' ? 'active' : ''}`} onClick={() => { setChequesTab('cleared'); if (clearedCheques.length === 0 && !clearedLoading) fetchClearedCheques(1, partySearchCleared); }}>
                  Cleared & Deleted
                  {clearedPagination.total > 0 && <span className="accounts-cheque-badge">{clearedPagination.total}</span>}
                </button>
              </div>
              {chequesTab === 'active' && (
                <>
                  <div className="cheques-search-row">
                    <input type="text" placeholder="Search by party name..." value={partySearchCheques} onChange={e => setPartySearchCheques(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearchActiveCheques()} className="cheques-search-input" />
                    <button type="button" className="accounts-btn accounts-btn--secondary btn-sm" onClick={handleSearchActiveCheques}>Search</button>
                  </div>
                  <p className="form-hint">By default this list shows 20 cheques per page. Search by party name below to find a specific party&apos;s cheques. Date shown is the cheque date entered when adding.</p>
                  {activeLoading ? (
                    <Loader message="Loading cheques…" variant="block" />
                  ) : activeCheques.length === 0 ? (
                    <div className="accounts-empty">
                      <HiDocumentText className="accounts-empty__icon" aria-hidden />
                      <p className="accounts-empty__text">No pending or upcoming cheques</p>
                    </div>
                  ) : (
                    <>
                      <div className="accounts-table-wrap">
                        <table className="accounts-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Party</th>
                              <th>Cheque #</th>
                              <th>Amount</th>
                              <th>Account</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activeCheques.map(c => (
                              <tr key={c.id}>
                                <td>{formatChequeDate(c.cheque_date)}</td>
                                <td>
                                  <span className="cheque-party-name">{c.party_name || '—'}</span>
                                  {(c.party_phone || c.party_city) && (
                                    <span className="cheque-party-details">
                                      {[c.party_phone, c.party_city].filter(Boolean).join(' · ')}
                                    </span>
                                  )}
                                </td>
                                <td className="accounts-table-mono">{c.cheque_number || '—'}</td>
                                <td className="accounts-table-amount">{formatPKR(c.amount)}</td>
                                <td>{c.account_name || '—'}</td>
                                <td><span className={`cheque-status cheque-status--${(c.status || '').toLowerCase()}`}>{c.status || '—'}</span></td>
                                <td>
                                  <span className="cheque-actions-cell">
                                    <button type="button" className="accounts-btn accounts-btn--primary btn-sm cheque-btn-clear" onClick={() => setApproveChequeId(c.id)} title="Turn cheque into cash – choose where to deposit">
                                      <HiCheck className="cheque-btn-clear__icon" aria-hidden /> Turn into cash
                                    </button>
                                    {confirmDeleteChequeId === c.id ? (
                                      <span className="cheque-delete-confirm">
                                        <button type="button" className="btn-primary btn-sm" onClick={() => handleDeleteCheque(c.id)} disabled={deletingChequeId === c.id}>{deletingChequeId === c.id ? 'Deleting…' : 'Confirm delete'}</button>
                                        <button type="button" className="btn-secondary btn-sm" onClick={() => setConfirmDeleteChequeId(null)}>Cancel</button>
                                      </span>
                                    ) : (
                                      <button type="button" className="accounts-btn accounts-btn--danger btn-sm" onClick={() => setConfirmDeleteChequeId(c.id)} title="Delete cheque">Delete</button>
                                    )}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {(activePagination.totalPages || 1) > 1 && (
                        <div className="cheques-pagination">
                          <button type="button" className="btn-secondary btn-sm" disabled={activePage <= 1} onClick={() => goToActivePage(activePage - 1)}>Previous</button>
                          <span className="cheques-pagination-info">Page {activePage} of {activePagination.totalPages || 1} ({activePagination.total || 0} total)</span>
                          <button type="button" className="btn-secondary btn-sm" disabled={activePage >= (activePagination.totalPages || 1)} onClick={() => goToActivePage(activePage + 1)}>Next</button>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
              {chequesTab === 'cleared' && (
                <>
                  <div className="cheques-search-row">
                    <input type="text" placeholder="Search by party name..." value={partySearchCleared} onChange={e => setPartySearchCleared(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearchClearedCheques()} className="cheques-search-input" />
                    <button type="button" className="accounts-btn accounts-btn--secondary btn-sm" onClick={handleSearchClearedCheques}>Search</button>
                  </div>
                  <p className="form-hint">Cleared (approved) and deleted cheques (20 per page). Date shown is the cheque date entered when adding.</p>
                  {clearedLoading ? (
                    <Loader message="Loading cleared cheques…" variant="block" />
                  ) : clearedCheques.length === 0 ? (
                    <div className="accounts-empty">
                      <HiDocumentText className="accounts-empty__icon" aria-hidden />
                      <p className="accounts-empty__text">No cleared or deleted cheques</p>
                    </div>
                  ) : (
                    <>
                      <div className="accounts-table-wrap">
                        <table className="accounts-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Party</th>
                              <th>Cheque #</th>
                              <th>Amount</th>
                              <th>Account</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {clearedCheques.map(c => (
                              <tr key={c.id}>
                                <td>{formatChequeDate(c.cheque_date)}</td>
                                <td>
                                  <span className="cheque-party-name">{c.party_name || '—'}</span>
                                  {(c.party_phone || c.party_city) && (
                                    <span className="cheque-party-details">
                                      {[c.party_phone, c.party_city].filter(Boolean).join(' · ')}
                                    </span>
                                  )}
                                </td>
                                <td className="accounts-table-mono">{c.cheque_number || '—'}</td>
                                <td className="accounts-table-amount">{formatPKR(c.amount)}</td>
                                <td>{c.account_name || '—'}</td>
                                <td><span className={`cheque-status cheque-status--${(c.status || '').toLowerCase()}`}>{c.status || '—'}</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {(clearedPagination.totalPages || 1) > 1 && (
                        <div className="cheques-pagination">
                          <button type="button" className="btn-secondary btn-sm" disabled={clearedPage <= 1} onClick={() => goToClearedPage(clearedPage - 1)}>Previous</button>
                          <span className="cheques-pagination-info">Page {clearedPage} of {clearedPagination.totalPages || 1} ({clearedPagination.total || 0} total)</span>
                          <button type="button" className="btn-secondary btn-sm" disabled={clearedPage >= (clearedPagination.totalPages || 1)} onClick={() => goToClearedPage(clearedPage + 1)}>Next</button>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
              <div className="modal-actions" style={{ marginTop: '1rem' }}>
                <button type="button" className="btn-secondary" onClick={() => { setShowChequesModal(false); setApproveChequeId(null); setConfirmDeleteChequeId(null); }}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Approve cheque: split payment into accounts */}
        {approveChequeId && (() => {
          const cheque = activeCheques.find(x => x.id === approveChequeId);
          const chequeAmount = cheque ? parseFloat(cheque.amount) || 0 : 0;
          const total = approveSplits.reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
          const accountOptions = effectiveBranchId ? accounts : (summary?.accounts || accounts);
          return (
            <div className="modal-overlay" onClick={() => !submitting && setApproveChequeId(null)}>
              <div className="modal-content accounts-modal accounts-approve-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Where to deposit?</h3>
                  <button type="button" className="modal-close" onClick={() => !submitting && setApproveChequeId(null)}><HiX /></button>
                </div>
                <p className="form-hint">This cheque is being turned into cash. Choose where to deposit the payment: you can add it to a single account or split it across multiple accounts. Party ledger will be updated and each selected account will be credited.</p>
                {cheque && (
                  <p className="approve-cheque-amount"><strong>Cheque amount:</strong> {formatPKR(chequeAmount)}</p>
                )}
                <form onSubmit={handleApproveCheque}>
                  <div className="form-group">
                    <div className="approve-splits-header">
                      <label>Credit to accounts</label>
                      <button
                        type="button"
                        className="accounts-btn accounts-btn--secondary btn-sm"
                        onClick={() => setApproveSplits(prev => [...prev, { accountId: '', amount: '' }])}
                      >
                        <HiPlus className="accounts-btn__icon" /> Add split
                      </button>
                    </div>
                    <div className="approve-splits-table-wrap">
                      <table className="approve-splits-table">
                        <thead>
                          <tr>
                            <th>Account</th>
                            <th>Payment</th>
                            <th className="approve-splits-col-action"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {approveSplits.map((split, idx) => {
                            const selectedInOtherRows = new Set(
                              approveSplits
                                .map((s, i) => (i !== idx && s.accountId ? String(s.accountId) : null))
                                .filter(Boolean)
                            );
                            const availableAccounts = accountOptions.filter(
                              a => String(a.id) === String(split.accountId) || !selectedInOtherRows.has(String(a.id))
                            );
                            const selectedAccount = split.accountId ? accountOptions.find(a => String(a.id) === String(split.accountId)) : null;
                            return (
                              <tr key={idx} className="approve-split-row">
                                <td className="approve-split-col-account">
                                  <select
                                    value={split.accountId}
                                    onChange={e => setApproveSplits(prev => {
                                      const next = [...prev];
                                      next[idx] = { ...next[idx], accountId: e.target.value };
                                      return next;
                                    })}
                                    className="approve-split-select"
                                  >
                                    <option value="">Select account</option>
                                    {availableAccounts.map(a => (
                                      <option key={a.id} value={a.id}>{a.name} ({a.type}) – {formatPKR(a.current_balance ?? a.currentBalance ?? 0)}</option>
                                    ))}
                                  </select>
                                  {selectedAccount && (
                                    <span className="approve-split-account-display">{selectedAccount.name}</span>
                                  )}
                                </td>
                                <td className="approve-split-col-payment">
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="Amount"
                                    value={split.amount}
                                    onChange={e => setApproveSplits(prev => {
                                      const next = [...prev];
                                      next[idx] = { ...next[idx], amount: e.target.value };
                                      return next;
                                    })}
                                    className="approve-split-input"
                                  />
                                  {selectedAccount && split.amount !== '' && !Number.isNaN(parseFloat(split.amount)) && (
                                    <span className="approve-split-payment-display">{formatPKR(parseFloat(split.amount))}</span>
                                  )}
                                </td>
                                <td className="approve-splits-col-action">
                                  {approveSplits.length > 1 && (
                                    <button
                                      type="button"
                                      className="btn-remove-split"
                                      onClick={() => setApproveSplits(prev => prev.filter((_, i) => i !== idx))}
                                      aria-label="Remove split"
                                    >
                                      <HiX />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <p className={`approve-splits-total ${Math.abs(total - chequeAmount) > 0.01 && total > 0 ? 'error' : ''}`}>
                      Total: {formatPKR(total)} {chequeAmount > 0 && (Math.abs(total - chequeAmount) > 0.01 ? ` (must equal ${formatPKR(chequeAmount)})` : '')}
                    </p>
                  </div>
                  {(() => {
                    const summaryRows = approveSplits
                      .filter(s => s.accountId && (parseFloat(s.amount) || 0) > 0)
                      .map(s => ({
                        account: accountOptions.find(a => String(a.id) === String(s.accountId)),
                        amount: parseFloat(s.amount) || 0
                      }))
                      .filter(r => r.account);
                    const summaryTotal = summaryRows.reduce((sum, r) => sum + r.amount, 0);
                    const isComplete = Math.abs(summaryTotal - chequeAmount) <= 0.01;
                    return (
                      <div className="approve-deposit-summary">
                        <h4 className="approve-deposit-summary-title">Deposit summary</h4>
                        <p className="approve-deposit-summary-cheque">Cheque amount: <strong>{formatPKR(chequeAmount)}</strong></p>
                        {summaryRows.length > 0 && (
                          <ul className="approve-deposit-summary-list">
                            {summaryRows.map((r, i) => (
                              <li key={i}>
                                <span className="approve-deposit-summary-account">{r.account.name}</span>
                                <span className="approve-deposit-summary-amount">{formatPKR(r.amount)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        <p className={`approve-deposit-summary-total ${!isComplete && summaryTotal > 0 ? 'error' : ''}`}>
                          Total to deposit: <strong>{formatPKR(summaryTotal)}</strong>
                          {chequeAmount > 0 && !isComplete && summaryTotal > 0 && (
                            <span className="approve-deposit-summary-warn"> (must equal {formatPKR(chequeAmount)})</span>
                          )}
                        </p>
                      </div>
                    );
                  })()}
                  <div className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={() => setApproveChequeId(null)}>Cancel</button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={submitting || (chequeAmount > 0 && Math.abs(total - chequeAmount) > 0.01)}
                      title={chequeAmount > 0 && Math.abs(total - chequeAmount) > 0.01 ? `Split total (${formatPKR(total)}) must equal cheque amount (${formatPKR(chequeAmount)})` : ''}
                    >
                      {submitting ? 'Depositing…' : 'Deposit'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          );
        })()}


        {/* Account list */}
        <section className="accounts-section accounts-section--list">
          <div className="accounts-section-header">
            <h2 className="accounts-section-title">Accounts</h2>
            <p className="accounts-section-desc">Cash and bank accounts for this branch.</p>
          </div>
          <div className="accounts-grid">
            {(effectiveBranchId ? accounts : (summary?.accounts || accounts)).map(acc => (
              <article key={acc.id} className={`account-tile account-tile--${acc.type}`} onClick={() => openTransactions(acc)}>
                <div className="account-tile__icon">
                  {acc.type === 'cash' ? <HiCash aria-hidden /> : <HiOfficeBuilding aria-hidden />}
                </div>
                <div className="account-tile__main">
                  <span className="account-tile__name">{acc.name}</span>
                  {acc.bank_name && <span className="account-tile__meta">{acc.bank_name}</span>}
                  <span className="account-tile__type">{acc.type}</span>
                </div>
                <div className="account-tile__balance">{formatPKR(acc.current_balance ?? acc.currentBalance ?? 0)}</div>
                <span className="account-tile__action">View transactions</span>
              </article>
            ))}
            {(!summary?.accounts?.length && !accounts.length) && (
              <div className="accounts-empty accounts-empty--cards">
                <HiOfficeBuilding className="accounts-empty__icon" aria-hidden />
                <p className="accounts-empty__text">No accounts yet</p>
                <p className="accounts-empty__hint">Add a bank account above, or record a payment to create the default Cash account.</p>
                <button type="button" className="accounts-btn accounts-btn--secondary" onClick={() => setShowAddAccount(true)} disabled={!hasBranches}>
                  <HiPlus className="accounts-btn__icon" /> Add account
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Add Account Modal */}
        {showAddAccount && (
          <div className="modal-overlay" onClick={() => !submitting && setShowAddAccount(false)}>
            <div className="modal-content accounts-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Add Account</h3>
                <button type="button" className="modal-close" onClick={() => !submitting && setShowAddAccount(false)}><HiX /></button>
              </div>
              {hasBranches && (
                <div className="add-account-branch-bar">
                  <span className="add-account-branch-label">Adding to branch</span>
                  <span className="add-account-branch-name">{currentBranch?.name ?? (effectiveBranchId ? 'Loading…' : 'Select a branch in the header')}</span>
                </div>
              )}
              <form onSubmit={handleAddAccount}>
                <div className="form-group">
                  <label>Type</label>
                  <select value={addAccountForm.type} onChange={e => setAddAccountForm(prev => ({ ...prev, type: e.target.value }))}>
                    <option value="bank">Bank</option>
                    <option value="cash">Cash</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Name *</label>
                  <input value={addAccountForm.name} onChange={e => setAddAccountForm(prev => ({ ...prev, name: e.target.value }))} placeholder={addAccountForm.type === 'cash' ? 'Cash' : 'e.g. HBL Main'} required />
                </div>
                {addAccountForm.type === 'bank' && (
                  <>
                    <div className="form-group">
                      <label>Bank name</label>
                      <input value={addAccountForm.bankName} onChange={e => setAddAccountForm(prev => ({ ...prev, bankName: e.target.value }))} placeholder="e.g. HBL" />
                    </div>
                    <div className="form-group">
                      <label>Account number</label>
                      <input value={addAccountForm.accountNumber} onChange={e => setAddAccountForm(prev => ({ ...prev, accountNumber: e.target.value }))} placeholder="Account #" />
                    </div>
                  </>
                )}
                <div className="form-group">
                  <label>Opening balance</label>
                  <input type="number" step="0.01" value={addAccountForm.openingBalance} onChange={e => setAddAccountForm(prev => ({ ...prev, openingBalance: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea value={addAccountForm.notes} onChange={e => setAddAccountForm(prev => ({ ...prev, notes: e.target.value }))} rows={2} />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowAddAccount(false)}>Cancel</button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={submitting || (isAdmin && !effectiveBranchId)}
                    title={isAdmin && !effectiveBranchId ? 'Select a branch in the header first' : ''}
                  >
                    {submitting ? 'Saving...' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Manual Entry Modal */}
        {showManualEntry && (
          <div className="modal-overlay" onClick={() => !submitting && setShowManualEntry(false)}>
            <div className="modal-content accounts-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Manual Entry</h3>
                <button type="button" className="modal-close" onClick={() => !submitting && setShowManualEntry(false)}><HiX /></button>
              </div>
              <p className="form-hint">Record expense, ATM withdrawal, or deposit (money in/out not linked to a party).</p>
              <form onSubmit={handleManualEntry}>
                <div className="form-group">
                  <label>Account *</label>
                  <select value={manualEntryForm.accountId} onChange={e => setManualEntryForm(prev => ({ ...prev, accountId: e.target.value }))} required>
                    <option value="">Select account</option>
                    {(effectiveBranchId ? accounts : (summary?.accounts || accounts)).map(a => (
                      <option key={a.id} value={a.id}>{a.name} ({a.type}) – {formatPKR(a.current_balance ?? a.currentBalance)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Type</label>
                  <select value={manualEntryForm.entryType} onChange={e => setManualEntryForm(prev => ({ ...prev, entryType: e.target.value }))}>
                    <option value="expense">Expense (outside business)</option>
                    <option value="atm_withdrawal">ATM withdrawal</option>
                    <option value="deposit">Deposit</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Direction</label>
                  <select value={manualEntryForm.direction} onChange={e => setManualEntryForm(prev => ({ ...prev, direction: e.target.value }))}>
                    <option value="debit">Money out (debit)</option>
                    <option value="credit">Money in (credit)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Amount *</label>
                  <input type="number" step="0.01" min="0.01" value={manualEntryForm.amount} onChange={e => setManualEntryForm(prev => ({ ...prev, amount: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input value={manualEntryForm.description} onChange={e => setManualEntryForm(prev => ({ ...prev, description: e.target.value }))} placeholder="e.g. ATM withdrawal for personal" />
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" value={manualEntryForm.transactionDate} onChange={e => setManualEntryForm(prev => ({ ...prev, transactionDate: e.target.value }))} />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowManualEntry(false)}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Record'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Transfer Modal */}
        {showTransfer && (
          <div className="modal-overlay" onClick={() => !submitting && setShowTransfer(false)}>
            <div className="modal-content accounts-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Transfer</h3>
                <button type="button" className="modal-close" onClick={() => !submitting && setShowTransfer(false)}><HiX /></button>
              </div>
              <form onSubmit={handleTransfer}>
                <div className="form-group">
                  <label>From account *</label>
                  <select
                    value={transferForm.fromAccountId}
                    onChange={e => {
                      const fromId = e.target.value;
                      setTransferForm(prev => ({
                        ...prev,
                        fromAccountId: fromId,
                        toAccountId: prev.toAccountId === fromId ? '' : prev.toAccountId
                      }));
                    }}
                    required
                  >
                    <option value="">Select</option>
                    {transferAccountList.map(a => (
                      <option key={a.id} value={a.id}>{getTransferAccountOptionLabel(a, true)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>To account *</label>
                  <select value={transferForm.toAccountId} onChange={e => setTransferForm(prev => ({ ...prev, toAccountId: e.target.value }))} required>
                    <option value="">Select</option>
                    {transferAccountList
                      .filter(a => String(a.id) !== String(transferForm.fromAccountId))
                      .map(a => (
                        <option key={a.id} value={a.id}>{getTransferAccountOptionLabel(a, true)}</option>
                      ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Amount *</label>
                  <input type="number" step="0.01" min="0.01" value={transferForm.amount} onChange={e => setTransferForm(prev => ({ ...prev, amount: e.target.value }))} required />
                  {transferAmountExceedsBalance && (
                    <p className="form-error transfer-amount-exceeds" role="alert">
                      Amount exceeds available balance ({formatPKR(transferFromBalance)}). Reduce the amount to continue.
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input value={transferForm.description} onChange={e => setTransferForm(prev => ({ ...prev, description: e.target.value }))} placeholder="Optional" />
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" value={transferForm.transactionDate} onChange={e => setTransferForm(prev => ({ ...prev, transactionDate: e.target.value }))} />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowTransfer(false)}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={submitting || transferAmountExceedsBalance}>{submitting ? 'Transferring...' : 'Transfer'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Transactions Modal */}
        {showTransactions && selectedAccount && (
          <div className="modal-overlay" onClick={() => setShowTransactions(false)}>
            <div className="modal-content transactions-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Transactions – {selectedAccount.name}</h3>
                <button type="button" className="modal-close" onClick={() => setShowTransactions(false)}><HiX /></button>
              </div>
              <div className="transactions-filters">
                <label>From</label>
                <input
                  type="date"
                  value={txFromDate}
                  onChange={e => setTxFromDate(e.target.value)}
                  className="transactions-date-input"
                />
                <label>To</label>
                <input
                  type="date"
                  value={txToDate}
                  onChange={e => setTxToDate(e.target.value)}
                  className="transactions-date-input"
                />
                <button type="button" className="transactions-apply-btn" onClick={applyTransactionFilters}>
                  Apply
                </button>
                <button
                  type="button"
                  className="transactions-apply-btn"
                  onClick={handleDownloadTransactionsPdf}
                  disabled={transactionsLoading || transactionsPdfLoading}
                  title="Download this account statement as PDF"
                >
                  {transactionsPdfLoading ? 'Preparing…' : 'Download PDF'}
                </button>
              </div>
              <div className="transactions-list">
                {transactionsLoading && <Loader message="Loading transactions…" variant="block" />}
                {!transactionsLoading && transactions.length === 0 && <p className="empty-state">No transactions in this period.</p>}
                {!transactionsLoading && transactions.map(tx => (
                  <div key={tx.id} className="transaction-row">
                    <span className="tx-date">{tx.transaction_date}</span>
                    <span className={`tx-type ${tx.type}`}>{formatType(tx.type)}</span>
                    <span className="tx-amount">{formatPKR(tx.amount)}</span>
                    <span className="tx-desc">{tx.description || tx.reference_type || '—'}</span>
                    <span className="tx-balance">{formatPKR(tx.balance_after)}</span>
                  </div>
                ))}
              </div>
              {!transactionsLoading && (txPagination.total_pages ?? txPagination.totalPages ?? 1) > 1 && (
                <div className="transactions-pagination">
                  <button
                    type="button"
                    className="transactions-page-btn"
                    disabled={txPage <= 1}
                    onClick={() => goToTransactionsPage(txPage - 1)}
                  >
                    Previous
                  </button>
                  <span className="transactions-page-info">
                    Page {txPage} of {txPagination.total_pages ?? txPagination.totalPages ?? 1}
                    {txPagination.total != null && ` (${txPagination.total} records)`}
                  </span>
                  <button
                    type="button"
                    className="transactions-page-btn"
                    disabled={txPage >= (txPagination.total_pages ?? txPagination.totalPages ?? 1)}
                    onClick={() => goToTransactionsPage(txPage + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Accounts;
